import React, { useMemo, useState } from 'react';
import { TextBox } from '../../../components/ui/TextBox';
import { Button } from '../../../components/ui/Button';

export type CreateViewProps = {
  form: {
    sku?: string;
    name: string;
    price: number;
    imageUrl?: string;
    description?: string;
  };
  setField: <K extends keyof CreateViewProps['form']>(key: K, value: CreateViewProps['form'][K]) => void;
  onSubmit: () => void;
  onCancel: () => void;
  loading?: boolean;
  error?: string | null;
  setImageFile?: (f: File | null) => void;
};

export const ProductCreateView: React.FC<CreateViewProps> = ({ form, setField, onSubmit, onCancel, loading, error, setImageFile }) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setImageFile?.(file);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(file ? URL.createObjectURL(file) : null);
  };

  const img = useMemo(() => previewUrl || form.imageUrl || '', [previewUrl, form.imageUrl]);

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="mb-3 text-xl font-semibold">New Product</h1>
      <div className="space-y-3">
        <TextBox label="SKU" value={form.sku || ''} onChange={e => setField('sku', e.target.value)} />
        <TextBox label="Name" value={form.name} onChange={e => setField('name', e.target.value)} required />
        <TextBox label="Price" type="number" value={String(form.price)} onChange={e => setField('price', Number(e.target.value))} required />
        <div>
          <label className="block text-sm font-medium text-foreground">Image</label>
          <input type="file" accept="image/*" onChange={onFileChange} className="mt-1 w-full" />
          {img && (
            <img src={img} alt="preview" className="mt-2 h-32 w-32 rounded object-cover border" />
          )}
        </div>
        <TextBox label="Description" value={form.description || ''} onChange={e => setField('description', e.target.value)} />
      </div>
      {error && <div className="mt-3 text-sm text-destructive">{error}</div>}
      <div className="mt-4 flex gap-2">
        <Button onClick={onSubmit} disabled={loading}>{loading ? 'Saving...' : 'Save'}</Button>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
};

export default ProductCreateView;
